import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { AuthUser, LoginCredentials } from '../types';

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

type SessionExpiredCallback = () => void;

class AuthService {
  private userPool: CognitoUserPool;
  private sessionExpiredCallbacks: Set<SessionExpiredCallback> = new Set();

  constructor() {
    this.userPool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: CLIENT_ID,
    });
  }

  async login(credentials: LoginCredentials): Promise<{ user: AuthUser; token: string }> {
    const username = credentials.username.toLowerCase();

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: credentials.password,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const idToken = session.getIdToken();
          const accessToken = session.getAccessToken();
          const groups = idToken.payload['cognito:groups'] || [];

          const user: AuthUser = {
            id: idToken.payload.sub,
            email: idToken.payload.email || '',
            username: accessToken.payload.username || username,
            groups,
            isRoot: groups.includes('root'),
          };

          const token = accessToken.getJwtToken();
          localStorage.setItem('cognito_token', token);

          resolve({ user, token });
        },
        onFailure: (err) => {
          reject(new Error(err.message || 'Authentication failed'));
        },
        newPasswordRequired: () => {
          reject(new Error('New password required. Please contact administrator.'));
        },
      });
    });
  }

  async logout(): Promise<void> {
    const cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem('cognito_token');
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    const cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return null;
    }

    return new Promise((resolve) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const idToken = session.getIdToken();
        const accessToken = session.getAccessToken();

        const groups = idToken.payload['cognito:groups'] || [];
        resolve({
          id: idToken.payload.sub,
          email: idToken.payload.email || '',
          username: accessToken.payload.username || '',
          groups,
          isRoot: groups.includes('root'),
        });
      });
    });
  }

  async getToken(): Promise<string | null> {
    const cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      return null;
    }

    return new Promise((resolve) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        resolve(session.getAccessToken().getJwtToken());
      });
    });
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('cognito_token');
    return !!token;
  }

  onSessionExpired(callback: SessionExpiredCallback): () => void {
    this.sessionExpiredCallbacks.add(callback);
    return () => {
      this.sessionExpiredCallbacks.delete(callback);
    };
  }

  private emitSessionExpired(): void {
    this.sessionExpiredCallbacks.forEach((callback) => callback());
  }

  forceLogout(): void {
    const cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem('cognito_token');
    this.emitSessionExpired();
  }
}

export const authService = new AuthService();
